import {
	ArcElement,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Title,
	Tooltip,
} from 'chart.js';
import React from 'react';
import { Bar, Line, Pie, Scatter } from 'react-chartjs-2';

import styles from './BarryChart.module.scss';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	ArcElement,
	Title,
	Tooltip,
	Legend,
);

interface ChartDataset {
	label: string;
	data: number[];
	backgroundColor: string[];
	borderColor: string[];
}

export interface VisualizationData {
	chartType: 'bar' | 'line' | 'pie' | 'table' | 'area' | 'scatter';
	title: string;
	description: string;
	chartConfig: {
		labels: string[];
		datasets: ChartDataset[];
		columns?: Array<{
			key: string;
			header: string;
			format: 'text' | 'currency' | 'number' | 'date' | 'percentage';
		}>;
	};
	insights: string[];
}

interface BarryChartProps {
	visualization: VisualizationData;
}

export const BarryChart: React.FC<BarryChartProps> = ({ visualization }) => {
	const { chartType, title, description, chartConfig, insights } = visualization;

	const chartData = {
		labels: chartConfig.labels,
		datasets: chartConfig.datasets,
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'top' as const,
			},
			title: {
				display: true,
				text: title,
			},
		},
	};

	const renderChart = () => {
		switch (chartType) {
			case 'bar':
				return <Bar data={chartData} options={options} />;
			case 'line':
			case 'area':
				return <Line data={chartData} options={options} />;
			case 'pie':
				return <Pie data={chartData} options={options} />;
			case 'scatter':
				return <Scatter data={chartData} options={options} />;
			default:
				return null;
		}
	};

	return (
		<div className={styles.chartContainer}>
			<p className={styles.description}>{description}</p>
			<div className={styles.chartWrapper}>{renderChart()}</div>
			{insights.length > 0 && (
				<div className={styles.insights}>
					<h4>Key Insights:</h4>
					<ul>
						{insights.map((insight, index) => (
							<li key={index}>{insight}</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};
